import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SR = typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null;
const NOISE = /^(terima kasih( kerana menonton)?|thank you( for watching)?|you|\.+)[.!\s]*$/i;
const WINDOW_MS = 6000;

export function useLiveTranscript({ stream, state, elapsedRef, mode = "mic" }) {
  const wsId = useAuth().workspace?.id;
  const [lines, setLines] = useState([]);
  const [interim, setInterim] = useState("");
  const [pending, setPending] = useState(0);
  const [error, setError] = useState("");
  const [lang, setLangState] = useState(() => localStorage.getItem("minitai-live-lang") || "ms-MY");
  const interimRef = useRef("");
  const chain = useRef(Promise.resolve());
  const lastText = useRef("");
  const useSR = !!SR && mode === "mic";
  const engine = useSR ? "browser" : "whisper";

  const uttStart = useRef(null);
  const addFinal = useCallback((text, at) => {
    const t = text.trim();
    const stamp = at ?? uttStart.current ?? elapsedRef.current;
    uttStart.current = null;
    if (!t || NOISE.test(t)) return;
    lastText.current = t;
    setLines((l) => [...l, { id: l.length, t: stamp, text: t }]);
  }, [elapsedRef]);

  const setLang = (v) => { localStorage.setItem("minitai-live-lang", v); setLangState(v); };
  const reset = () => { setLines([]); setInterim(""); setError(""); interimRef.current = ""; };

  useEffect(() => {
    if (!useSR || state !== "recording") return;
    let alive = true;
    const r = new SR();
    r.continuous = true;
    r.interimResults = true;
    r.lang = lang;
    r.onresult = (e) => {
      let tmp = "";
      if (uttStart.current === null) uttStart.current = elapsedRef.current;
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) addFinal(e.results[i][0].transcript);
        else tmp += e.results[i][0].transcript;
      }
      interimRef.current = tmp.trim();
      setInterim(interimRef.current);
    };
    r.onerror = (e) => {
      if (["not-allowed", "service-not-allowed"].includes(e.error)) { alive = false; setError("denied"); }
      else if (e.error === "network") setError("network");
    };
    r.onstart = () => setError("");
    r.onend = () => { if (alive) try { r.start(); } catch { /* already started */ } };
    try { r.start(); } catch { setError("start"); }
    return () => {
      alive = false;
      r.onend = null;
      r.abort();
      if (interimRef.current) addFinal(interimRef.current);
      interimRef.current = "";
      setInterim("");
    };
  }, [state, lang, addFinal, elapsedRef, useSR]);

  useEffect(() => {
    if (useSR || state !== "recording" || !stream) return;
    let alive = true;
    let mr;
    const send = (blob, at) => {
      if (blob.size < 2000) return;
      setPending((n) => n + 1);
      chain.current = chain.current.then(async () => {
        const fd = new FormData();
        fd.append("file", blob, "live");
        fd.append("language", lang.slice(0, 2));
        fd.append("prompt", lastText.current);
        fd.append("workspace_id", wsId);
        try { addFinal((await api.post("/live/transcribe", fd)).data.text, at); setError(""); } catch (e) { setError(e.response?.status === 402 ? "plan" : "network"); }
        setPending((n) => n - 1);
      });
    };
    const cycle = () => {
      if (!alive) return;
      const parts = [];
      const at = elapsedRef.current;
      mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => e.data.size && parts.push(e.data);
      mr.onstop = () => { send(new Blob(parts, { type: mr.mimeType || "audio/webm" }), at); cycle(); };
      mr.start();
      setTimeout(() => mr.state !== "inactive" && mr.stop(), WINDOW_MS);
    };
    cycle();
    return () => { alive = false; if (mr && mr.state !== "inactive") mr.stop(); };
  }, [state, stream, lang, addFinal, elapsedRef, useSR]);

  return { lines, interim, pending, error, lang, setLang, reset, engine };
}
